/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BarChart2,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  FileText,
  Calendar,
  ShoppingBag,
  BookOpen,
  CreditCard,
  TrendingUp,
  Bell,
  Menu,
  X,
  User,
  BookMarked,
  CreditCard as CreditCardIcon,
  BarChart,
  Notebook,
  Clock,
  ClipboardCheck,
  UserCog,
  Download,
  UserPlus,
  MessageSquare,
  Building2,
  Layers,
  Shield,
  Megaphone,
  Wrench,
  KeyRound,
  HardDrive,
  Bus,
  HandCoins,
  LifeBuoy
} from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { normalizeRole } from "../utils/authUtils";
import "../styles/sidebar.css";

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  role?: string;
}

const MENU_ROUTE_MAP: Record<string, string[]> = {
  classes: ["/allclasses", "/subjects", "/classsubjects", "/classteacher", "/studentsinclass", "/academics/promotion"],
  academics: ["/student/subjects", "/student/timetable", "/student/attendance"],
  settings: ["/settings", "/settings/profile", "/settings/export", "/settings/subscription", "/superadmin/system-settings"],
  finance: [
    "/finance/feessummary",
    "/finance/fees",
    "/finance/payments",
    "/payroll-management",
    "/payroll/management",
    "/payroll/periods",
    "/payroll/components",
    "/my-payroll",
    "/finance/expenses",
    "/reports/finance",
    "/student/fees",
    "/superadmin/subscriptions",
    "/superadmin/subscription-enforcement",
    "/superadmin/financial-overview",
    "/superadmin/analytics"
  ]
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen, role: propRole }) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const location = useLocation();
  const previousPathname = useRef(location.pathname);
  const role = normalizeRole(propRole || localStorage.getItem("role")) || "student";
  const { branding, tenant, hasModule } = useTenant();

  const schoolLogo = branding.logoUrl;
  const schoolName = branding.displayName;

  // Update document title when school name is available
  useEffect(() => {
    if (role === "superadmin") {
      document.title = "Super Admin | Platform Console";
    } else if (schoolName) {
      document.title = branding.portalTitle;
    }
  }, [branding.portalTitle, role, schoolName]);

  useEffect(() => {
    if (!openMenu) return;
    if (previousPathname.current === location.pathname) return;
    const activePaths = MENU_ROUTE_MAP[openMenu] || [];
    const isStillWithinOpenMenu = activePaths.some((path) => isActive(path));
    if (!isStillWithinOpenMenu) {
      setOpenMenu(null);
    }
    previousPathname.current = location.pathname;
  }, [location.pathname, openMenu]);

  useEffect(() => {
    previousPathname.current = location.pathname;
  }, [location.pathname]);


  const toggleDropdown = (menu: string) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");
  const isExactActive = (path: string) => location.pathname === path;

  const getDashboardPath = () => {
    switch (role) {
      case "superadmin": return "/superadmin";
      case "admin": return "/admindashboard";
      case "headteacher": return "/headteacher";
      case "teacher": return "/teacherdashboard";
      case "student": return "/dashboard";
      default: return "/dashboard";
    }
  };

  const isDropdownParentActive = (paths: string[]) => {
    return paths.some(path => isActive(path));
  };
  const canUse = (moduleKey?: string) => role === "superadmin" || !moduleKey || hasModule(moduleKey);
  const hasStudentAcademicMenu = canUse("results") || canUse("classes") || canUse("timetable") || canUse("attendance");
  const hasStudentFinanceMenu = canUse("fees") || canUse("uniform_orders");
  const hasTeacherAcademicMenu = canUse("results") || canUse("timetable") || canUse("attendance");
  const canManageUsers = canUse("students") || canUse("teachers");
  const hasAdminFinanceMenu = canUse("fees") || canUse("payroll") || canUse("expenses") || canUse("financial_reports");
  const hasHeadteacherFinanceMenu = false;
  const hasTeacherFinanceMenu = role === "teacher" && canUse("payroll");

  return (
    <>
      <button
        className={`toggle-btn ${isOpen ? "md:hidden" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle Sidebar"
      >
        {isOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          {role !== "superadmin" && (
            <Link to={getDashboardPath()} className="logo-container">
              {schoolLogo ? (
                <img
                  src={schoolLogo}
                  alt={`${schoolName} logo`}
                  className="sidebar-logo"
                  onError={(e) => {
                    // If the logo URL fails to load, hide the image — school name will show instead
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  className="sidebar-logo sidebar-logo-fallback"
                  style={{ backgroundColor: branding.primarySoft, color: branding.primaryColor }}
                >
                  {branding.initials}
                </div>
              )}
              {schoolName && <h3 className="sidebar-school-name">{schoolName}</h3>}
            </Link>
          )}
          {role === "superadmin" && (
            <div className="logo-container">
              <h3 className="sidebar-school-name">Enterprise School Portal Platform</h3>
            </div>
          )}
        </div>

        <ul className="sidebar-menu">
          {/* Dashboard */}
          {role !== "superadmin" && canUse("dashboard") && (
            <li className={`menu-item ${isExactActive(getDashboardPath()) ? "active" : ""}`}>
              <Link to={getDashboardPath()}>
                <LayoutDashboard size={18} className="icon" />
                <span>Dashboard</span>
              </Link>
            </li>
          )}

          {/* SUPERADMIN MENU */}
          {role === "superadmin" && (
            <>
              <li className="sidebar-section-header">SUPER ADMIN</li>
              <li className={`menu-item ${isExactActive("/superadmin") ? "active" : ""}`}>
                <Link to="/superadmin">
                  <LayoutDashboard size={18} className="icon" />
                  <span>Super Admin Dashboard</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/schools") ? "active" : ""}`}>
                <Link to="/superadmin/schools">
                  <Building2 size={18} className="icon" />
                  <span>Manage Schools</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/users") ? "active" : ""}`}>
                <Link to="/superadmin/users">
                  <Users size={18} className="icon" />
                  <span>Manage Users</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/audit-trail") ? "active" : ""}`}>
                <Link to="/superadmin/audit-trail">
                  <FileText size={18} className="icon" />
                  <span>Audit Trail</span>
                </Link>
              </li>
              <li className={`menu-item dropdown-parent ${openMenu === "finance" || isDropdownParentActive(MENU_ROUTE_MAP.finance) ? "active" : ""}`}>
                <div className="menu-link" onClick={() => toggleDropdown("finance")}>
                  <CreditCard size={18} className="icon" />
                  <span>Finance</span>
                  {openMenu === "finance" ? <ChevronDown size={14} className="chevron" /> : <ChevronRight size={14} className="chevron" />}
                </div>
                <ul className={`sidebar-dropdown ${openMenu === "finance" ? "open" : ""}`}>
                  <li className={`submenu-item ${isActive("/superadmin/subscriptions") ? "active" : ""}`}>
                    <Link to="/superadmin/subscriptions">
                      <CreditCard size={16} className="icon" />
                      <span>Subscriptions</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/superadmin/subscription-enforcement") ? "active" : ""}`}>
                    <Link to="/superadmin/subscription-enforcement">
                      <Shield size={16} className="icon" />
                      <span>Subscription Enforcement</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/superadmin/financial-overview") ? "active" : ""}`}>
                    <Link to="/superadmin/financial-overview">
                      <TrendingUp size={16} className="icon" />
                      <span>Financial Overview</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/superadmin/analytics") ? "active" : ""}`}>
                    <Link to="/superadmin/analytics">
                      <BarChart size={16} className="icon" />
                      <span>Growth Analytics</span>
                    </Link>
                  </li>
                  {canUse("expenses") && (
                    <li className={`submenu-item ${isActive("/finance/expenses") ? "active" : ""}`}>
                      <Link to="/finance/expenses">
                        <HandCoins size={16} className="icon" />
                        <span>School Expenses</span>
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
              <li className={`menu-item ${isActive("/superadmin/subjects") ? "active" : ""}`}>
                <Link to="/superadmin/subjects">
                  <BookOpen size={18} className="icon" />
                  <span>Global Subjects</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/classes") ? "active" : ""}`}>
                <Link to="/superadmin/classes">
                  <Layers size={18} className="icon" />
                  <span>Global Classes</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/roles-permissions") ? "active" : ""}`}>
                <Link to="/superadmin/roles-permissions">
                  <KeyRound size={18} className="icon" />
                  <span>Roles & Permissions</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/support") ? "active" : ""}`}>
                <Link to="/superadmin/support">
                  <MessageSquare size={18} className="icon" />
                  <span>Support Tickets</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/maintenance") ? "active" : ""}`}>
                <Link to="/superadmin/maintenance">
                  <Wrench size={18} className="icon" />
                  <span>Maintenance</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/backups") ? "active" : ""}`}>
                <Link to="/superadmin/backups">
                  <HardDrive size={18} className="icon" />
                  <span>Backups</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/broadcasts") ? "active" : ""}`}>
                <Link to="/superadmin/broadcasts">
                  <Megaphone size={18} className="icon" />
                  <span>Broadcasts</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/superadmin/integrations") ? "active" : ""}`}>
                <Link to="/superadmin/integrations">
                  <Layers size={18} className="icon" />
                  <span>Integrations</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/events") ? "active" : ""}`}>
                <Link to="/events">
                  <Calendar size={18} className="icon" />
                  <span>School Calendar</span>
                </Link>
              </li>
            </>
          )}

          {/* ADMIN MENU */}
          {role === "admin" && (
            <>
              <li className="sidebar-section-header">ADMINISTRATIVE</li>

              {canManageUsers && (
              <li className={`menu-item ${isActive("/add-user") ? "active" : ""}`}>
                <Link to="/add-user">
                  <UserPlus size={18} className="icon" />
                  <span>Add User</span>
                </Link>
              </li>
              )}
              {canUse("students") && (
              <li className={`menu-item ${isActive("/students") ? "active" : ""}`}>
                <Link to="/students">
                  <GraduationCap size={18} className="icon" />
                  <span>Students</span>
                </Link>
              </li>
              )}
              {canUse("teachers") && (
              <li className={`menu-item ${isActive("/users") ? "active" : ""}`}>
                <Link to="/users">
                  <Users size={18} className="icon" />
                  <span>Employees</span>
                </Link>
              </li>
              )}

              {canUse("classes") && (
              <li className={`menu-item dropdown-parent ${openMenu === "classes" || isDropdownParentActive(["/allclasses", "/subjects", "/classsubjects", "/classteacher", "/studentsinclass", "/academics/promotion"]) ? "active" : ""}`}>
                <div className="menu-link" onClick={() => toggleDropdown("classes")}>
                  <GraduationCap size={18} className="icon" />
                  <span>Classes</span>
                  {openMenu === "classes" ? <ChevronDown size={14} className="chevron" /> : <ChevronRight size={14} className="chevron" />}
                </div>
                <ul className={`sidebar-dropdown ${openMenu === "classes" ? "open" : ""}`}>
                  <li className={`submenu-item ${isActive("/allclasses") ? "active" : ""}`}>
                    <Link to="/allclasses">
                      <BookMarked size={16} className="icon" />
                      <span>All Classes</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/subjects") ? "active" : ""}`}>
                    <Link to="/subjects">
                      <BookOpen size={16} className="icon" />
                      <span>Subjects</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/classsubjects") ? "active" : ""}`}>
                    <Link to="/classsubjects">
                      <Notebook size={16} className="icon" />
                      <span>Class Subjects</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/classteacher") ? "active" : ""}`}>
                    <Link to="/classteacher">
                      <User size={16} className="icon" />
                      <span>Class Teachers</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/studentsinclass") ? "active" : ""}`}>
                    <Link to="/studentsinclass">
                      <Users size={16} className="icon" />
                      <span>Students In Class</span>
                    </Link>
                  </li>
                  <li className={`submenu-item ${isActive("/academics/promotion") ? "active" : ""}`}>
                    <Link to="/academics/promotion">
                      <GraduationCap size={16} className="icon" />
                      <span>Learner Promotion</span>
                    </Link>
                  </li>
                </ul>
              </li>
              )}

              {canUse("messaging") && (
              <li className={`menu-item ${isActive("/messages") || isActive("/messages/") ? "active" : ""}`}>
                <Link to="/messages">
                  <MessageSquare size={18} className="icon" />
                  <span>Messages</span>
                </Link>
              </li>
              )}

              {canUse("uniform_orders") && (
              <li className={`menu-item ${isActive("/shop/uniform-orders") ? "active" : ""}`}>
                <Link to="/shop/uniform-orders">
                  <ShoppingBag size={18} className="icon" />
                  <span>Uniform Orders</span>
                </Link>
              </li>
              )}

              {hasAdminFinanceMenu && (
              <li className={`menu-item dropdown-parent ${openMenu === "finance" || isDropdownParentActive(MENU_ROUTE_MAP.finance) ? "active" : ""}`}>
                <div className="menu-link" onClick={() => toggleDropdown("finance")}>
                  <CreditCard size={18} className="icon" />
                  <span>Finance</span>
                  {openMenu === "finance" ? <ChevronDown size={14} className="chevron" /> : <ChevronRight size={14} className="chevron" />}
                </div>
                <ul className={`sidebar-dropdown ${openMenu === "finance" ? "open" : ""}`}>
                  {canUse("fees") && (
                  <li className={`submenu-item ${isActive("/finance/feessummary") ? "active" : ""}`}>
                    <Link to="/finance/feessummary">
                      <CreditCard size={16} className="icon" />
                      <span>Fees & Payments</span>
                    </Link>
                  </li>
                  )}
                  {canUse("payroll") && (
                  <li className={`submenu-item ${isActive("/payroll-management") ? "active" : ""}`}>
                    <Link to="/payroll-management">
                      <CreditCard size={16} className="icon" />
                      <span>Payroll Management</span>
                    </Link>
                  </li>
                  )}
                  {canUse("expenses") && (
                  <li className={`submenu-item ${isActive("/finance/expenses") ? "active" : ""}`}>
                    <Link to="/finance/expenses">
                      <HandCoins size={16} className="icon" />
                      <span>School Expenses</span>
                    </Link>
                  </li>
                  )}
                  {canUse("financial_reports") && (
                  <li className={`submenu-item ${isActive("/reports/finance") ? "active" : ""}`}>
                    <Link to="/reports/finance">
                      <CreditCardIcon size={16} className="icon" />
                      <span>Financial Reports</span>
                    </Link>
                  </li>
                  )}
                </ul>
              </li>
              )}

              {canUse("transport") && (
              <li className={`menu-item ${isActive("/transport") ? "active" : ""}`}>
                <Link to="/transport">
                  <Bus size={18} className="icon" />
                  <span>Transport</span>
                </Link>
              </li>
              )}
              <li className={`menu-item ${isActive("/events") ? "active" : ""}`}>
                <Link to="/events">
                  <Calendar size={18} className="icon" />
                  <span>School Calendar</span>
                </Link>
              </li>

            </>
          )}

          {/* HEADTEACHER MENU */}
          {role === "headteacher" && (
            <>
              <li className="sidebar-section-header">HEADTEACHER</li>
              {canUse("students") && (
              <li className={`menu-item ${isActive("/headteacher/learners") ? "active" : ""}`}>
                <Link to="/headteacher/learners">
                  <Users size={18} className="icon" />
                  <span>Learners</span>
                </Link>
              </li>
              )}
              {canUse("teachers") && (
              <li className={`menu-item ${isActive("/headteacher/teachers") ? "active" : ""}`}>
                <Link to="/headteacher/teachers">
                  <GraduationCap size={18} className="icon" />
                  <span>Teachers</span>
                </Link>
              </li>
              )}
              {canUse("classes") && (
              <>
              <li className={`menu-item ${isActive("/headteacher/classes") ? "active" : ""}`}>
                <Link to="/headteacher/classes">
                  <BookOpen size={18} className="icon" />
                  <span>Classes</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/headteacher/subjects") ? "active" : ""}`}>
                <Link to="/headteacher/subjects">
                  <BookMarked size={18} className="icon" />
                  <span>Subjects</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/headteacher/class-subjects") ? "active" : ""}`}>
                <Link to="/headteacher/class-subjects">
                  <Notebook size={18} className="icon" />
                  <span>Class Subjects</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/headteacher/class-teachers") ? "active" : ""}`}>
                <Link to="/headteacher/class-teachers">
                  <User size={18} className="icon" />
                  <span>Class Teachers</span>
                </Link>
              </li>
              <li className={`menu-item ${isActive("/academics/promotion") ? "active" : ""}`}>
                <Link to="/academics/promotion">
                  <GraduationCap size={18} className="icon" />
                  <span>Learner Promotion</span>
                </Link>
              </li>
              </>
              )}
              {canUse("results") && (
              <li className={`menu-item ${isActive("/headteacher/results") ? "active" : ""}`}>
                <Link to="/headteacher/results">
                  <FileText size={18} className="icon" />
                  <span>Results Oversight</span>
                </Link>
              </li>
              )}
              {canUse("attendance") && (
              <li className={`menu-item ${isActive("/headteacher/attendance") ? "active" : ""}`}>
                <Link to="/headteacher/attendance">
                  <ClipboardCheck size={18} className="icon" />
                  <span>Attendance</span>
                </Link>
              </li>
              )}
              {canUse("report_cards") && (
              <li className={`menu-item ${isActive("/headteacher/report-cards") ? "active" : ""}`}>
                <Link to="/headteacher/report-cards">
                  <FileText size={18} className="icon" />
                  <span>Report Cards</span>
                </Link>
              </li>
              )}
              {canUse("messaging") && (
              <li className={`menu-item ${isActive("/messages") || isActive("/messages/") ? "active" : ""}`}>
                <Link to="/messages">
                  <MessageSquare size={18} className="icon" />
                  <span>Messages</span>
                </Link>
              </li>
              )}
              {hasHeadteacherFinanceMenu && (
              <li className={`menu-item dropdown-parent ${openMenu === "finance" || isDropdownParentActive(MENU_ROUTE_MAP.finance) ? "active" : ""}`}>
                <div className="menu-link" onClick={() => toggleDropdown("finance")}>
                  <CreditCard size={18} className="icon" />
                  <span>Finance</span>
                  {openMenu === "finance" ? <ChevronDown size={14} className="chevron" /> : <ChevronRight size={14} className="chevron" />}
                </div>
                <ul className={`sidebar-dropdown ${openMenu === "finance" ? "open" : ""}`}>
                  {canUse("payroll") && (
                  <li className={`submenu-item ${isActive("/my-payroll") ? "active" : ""}`}>
                    <Link to="/my-payroll">
                      <CreditCard size={16} className="icon" />
                      <span>My Payroll</span>
                    </Link>
                  </li>
                  )}
                </ul>
              </li>
              )}
              {canUse("notices") && (
              <li className={`menu-item ${isActive("/headteacher/notices") ? "active" : ""}`}>
                <Link to="/headteacher/notices">
                  <Bell size={18} className="icon" />
                  <span>Notices</span>
                </Link>
              </li>
              )}
              <li className={`menu-item ${isActive("/events") ? "active" : ""}`}>
                <Link to="/events">
                  <Calendar size={18} className="icon" />
                  <span>School Calendar</span>
                </Link>
              </li>
            </>
          )}

          {/* STUDENT MENU - Students have access to grades, attendance, timetable, subjects, fees, uniform, reports, calendar */}
          {role === "student" && (
            <>
              {hasStudentAcademicMenu && <li className="sidebar-section-header">MY ACADEMICS</li>}
              {canUse("results") && (
              <li className={`menu-item ${isActive("/my-grades") ? "active" : ""}`}>
                <Link to="/my-grades">
                  <BarChart2 size={18} className="icon" />
                  <span>My Grades</span>
                </Link>
              </li>
              )}

              {(canUse("classes") || canUse("timetable") || canUse("attendance")) && (
              <li className={`menu-item dropdown-parent ${openMenu === "academics" || isDropdownParentActive(["/student/subjects", "/student/timetable", "/student/attendance"]) ? "active" : ""}`}>
                <div className="menu-link" onClick={() => toggleDropdown("academics")}>
                  <BookOpen size={18} className="icon" />
                  <span>Academics</span>
                  {openMenu === "academics" ? <ChevronDown size={14} className="chevron" /> : <ChevronRight size={14} className="chevron" />}
                </div>
                <ul className={`sidebar-dropdown ${openMenu === "academics" ? "open" : ""}`}>
                  {canUse("classes") && (
                  <li className={`submenu-item ${isActive("/student/subjects") ? "active" : ""}`}>
                    <Link to="/student/subjects">
                      <BookOpen size={16} className="icon" />
                      <span>My Subjects</span>
                    </Link>
                  </li>
                  )}
                  {canUse("timetable") && (
                  <li className={`submenu-item ${isActive("/student/timetable") ? "active" : ""}`}>
                    <Link to="/student/timetable">
                      <Clock size={16} className="icon" />
                      <span>My Timetable</span>
                    </Link>
                  </li>
                  )}
                  {canUse("attendance") && (
                  <li className={`submenu-item ${isActive("/student/attendance") ? "active" : ""}`}>
                    <Link to="/student/attendance">
                      <ClipboardCheck size={16} className="icon" />
                      <span>My Attendance</span>
                    </Link>
                  </li>
                  )}
                </ul>
              </li>
              )}

              <li className={`menu-item ${isActive("/student/calendar") ? "active" : ""}`}>
                <Link to="/student/calendar">
                  <Calendar size={18} className="icon" />
                  <span>School Calendar</span>
                </Link>
              </li>

              {canUse("messaging") && <li className="sidebar-section-header">MESSAGES</li>}
              {canUse("messaging") && (
              <li className={`menu-item ${isActive("/messages") || isActive("/messages/") ? "active" : ""}`}>
                <Link to="/messages">
                  <MessageSquare size={18} className="icon" />
                  <span>Messages</span>
                </Link>
              </li>
              )}

              {hasStudentFinanceMenu && <li className="sidebar-section-header">FINANCE & SHOP</li>}
              {canUse("fees") && (
              <li className={`menu-item dropdown-parent ${openMenu === "finance" || isDropdownParentActive(MENU_ROUTE_MAP.finance) ? "active" : ""}`}>
                <div className="menu-link" onClick={() => toggleDropdown("finance")}>
                  <CreditCard size={18} className="icon" />
                  <span>Finance</span>
                  {openMenu === "finance" ? <ChevronDown size={14} className="chevron" /> : <ChevronRight size={14} className="chevron" />}
                </div>
                <ul className={`sidebar-dropdown ${openMenu === "finance" ? "open" : ""}`}>
                  <li className={`submenu-item ${isActive("/student/fees") ? "active" : ""}`}>
                    <Link to="/student/fees">
                      <CreditCard size={16} className="icon" />
                      <span>Fees & Payments</span>
                    </Link>
                  </li>
                </ul>
              </li>
              )}
              {canUse("uniform_orders") && (
              <li className={`menu-item ${isActive("/student/uniform") ? "active" : ""}`}>
                <Link to="/student/uniform">
                  <ShoppingBag size={18} className="icon" />
                  <span>Uniform Shop</span>
                </Link>
              </li>
              )}
            </>
          )}

          {/* TEACHER MENU - Classes */}
          {(role === "teacher" || role === "admin") && (
            <>
              {hasTeacherAcademicMenu && <li className="sidebar-section-header">ACADEMIC</li>}
              {canUse("results") && (
              <li className={`menu-item ${isActive("/record-assessments") ? "active" : ""}`}>
                <Link to="/record-assessments">
                  <BookOpen size={18} className="icon" />
                  <span>Record Assessments</span>
                </Link>
              </li>
              )}
              {canUse("results") && (
              <li className={`menu-item ${isActive("/view-results") ? "active" : ""}`}>
                <Link to="/view-results">
                  <FileText size={18} className="icon" />
                  <span>View Results</span>
                </Link>
              </li>
              )}
              {canUse("timetable") && (
              <li className={`menu-item ${isActive("/timetable") ? "active" : ""}`}>
                <Link to="/timetable">
                  <Calendar size={18} className="icon" />
                  <span>Timetable</span>
                </Link>
              </li>
              )}
              {role === "teacher" && (
                <>
                  <li className={`menu-item ${isActive("/events") ? "active" : ""}`}>
                    <Link to="/events">
                      <Calendar size={18} className="icon" />
                      <span>School Calendar</span>
                    </Link>
                  </li>
                  {canUse("messaging") && (
                  <li className={`menu-item ${isActive("/messages") || isActive("/messages/") ? "active" : ""}`}>
                    <Link to="/messages">
                      <MessageSquare size={18} className="icon" />
                      <span>Messages</span>
                    </Link>
                  </li>
                  )}
                </>
              )}
            </>
          )}

          {/* ATTENDANCE REGISTER - Admin & Teacher */}
          {(role === "admin" || role === "teacher") && canUse("attendance") && (
            <li className={`menu-item ${isActive("/attendance") ? "active" : ""}`}>
              <Link to="/attendance">
                <ClipboardCheck size={18} className="icon" />
                <span>Attendance Register</span>
              </Link>
            </li>
          )}

          {/* LIBRARY - Admin, Headteacher, Teacher */}
          {(role === "admin" || role === "headteacher" || role === "teacher") && canUse("library") && (
            <li className={`menu-item ${isActive("/library") ? "active" : ""}`}>
              <Link to="/library">
                <BookOpen size={18} className="icon" />
                <span>Library</span>
              </Link>
            </li>
          )}

          {/* REPORT CARDS - Admin */}
          {role === "admin" && canUse("report_cards") && (
            <li className={`menu-item ${isActive("/report-cards/viewer") ? "active" : ""}`}>
              <Link to="/report-cards/viewer">
                <FileText size={18} className="icon" />
                <span>Report Cards</span>
              </Link>
            </li>
          )}

          {/* SETTINGS */}
          <li className="sidebar-section-header">SETTINGS</li>
          <li className={`menu-item dropdown-parent ${openMenu === "settings" || isDropdownParentActive(["/settings", "/settings/profile", "/settings/export"]) ? "active" : ""}`}>
            <div className="menu-link" onClick={() => toggleDropdown("settings")}>
              <Settings size={18} className="icon" />
              <span>Settings</span>
              {openMenu === "settings" ? <ChevronDown size={14} className="chevron" /> : <ChevronRight size={14} className="chevron" />}
            </div>
            <ul className={`sidebar-dropdown ${openMenu === "settings" ? "open" : ""}`}>
              {role === "admin" && (
                <li className={`submenu-item ${isActive("/settings") ? "active" : ""}`}>
                  <Link to="/settings">
                    <Settings size={16} className="icon" />
                    <span>School Settings</span>
                  </Link>
                </li>
              )}
              {role === "admin" && !tenant?.is_subscription_exempt && (
                <li className={`submenu-item ${isActive("/settings/subscription") ? "active" : ""}`}>
                  <Link to="/settings/subscription">
                    <CreditCard size={16} className="icon" />
                    <span>Subscription</span>
                  </Link>
                </li>
              )}
              <li className={`submenu-item ${isActive("/settings/profile") ? "active" : ""}`}>
                <Link to="/settings/profile">
                  <UserCog size={16} className="icon" />
                  <span>Profile</span>
                </Link>
              </li>
              {role === "superadmin" && (
                <li className={`submenu-item ${isActive("/superadmin/system-settings") ? "active" : ""}`}>
                  <Link to="/superadmin/system-settings">
                    <Settings size={16} className="icon" />
                    <span>System Settings</span>
                  </Link>
                </li>
              )}
              {(role === "admin" || role === "headteacher" || role === "teacher") && (
                <li className={`submenu-item ${isActive("/support") ? "active" : ""}`}>
                  <Link to="/support">
                    <LifeBuoy size={16} className="icon" />
                    <span>Support Desk</span>
                  </Link>
                </li>
              )}
              {role === "admin" && (
                <li className={`submenu-item ${isActive("/settings/export") ? "active" : ""}`}>
                  <Link to="/settings/export">
                    <Download size={16} className="icon" />
                    <span>Export Data</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>

          {/* LOGOUT */}
          <li className="sidebar-logout">
            <Link
              to="/signin"
              onClick={(e) => {
                if (!window.confirm("Are you sure you want to log out?")) {
                  e.preventDefault();
                  return;
                }
                localStorage.removeItem("token");
                localStorage.removeItem("userName");
                localStorage.removeItem("role");
                localStorage.removeItem("profilePicture");
                localStorage.removeItem("userTitle");
                localStorage.removeItem("userId");
                localStorage.removeItem("subscriptionBlocked");
                localStorage.removeItem("subscriptionBlockedReason");
                localStorage.removeItem("subscriptionBlockedMessage");
                localStorage.removeItem("userCode");
              }}
            >
              <LogOut size={18} className="icon" />
              <span>Logout</span>
            </Link>
          </li>
        </ul>
      </aside>
    </>
  );
};

export default Sidebar;
